'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { adminContentService, ManagedMediaCoverage } from '@/lib/services/adminContentService';

const MediaCoverageSection: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('All');
  const [mediaCoverage, setMediaCoverage] = useState<ManagedMediaCoverage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await adminContentService.getMediaCoverage();
      setMediaCoverage(data.filter(m => m.is_visible));
    } catch (e) {
      console.warn('Failed to load media coverage');
    } finally {
      setLoading(false);
    }
  };

  const coverageTypes = ['All', 'TV', 'Print', 'Online', 'Podcast', 'Radio'];

  const filteredCoverage = mediaCoverage.filter(item => selectedType === 'All' || item.type === selectedType);

  if (!isHydrated || loading) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3 mx-auto" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-96 bg-muted rounded" />)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-6 py-3 rounded-full mb-6">
            <Icon name="TvIcon" size={24} className="text-primary" />
            <span className="text-primary font-cta text-sm font-bold">Media Coverage</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">Featured in Global Media</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-body">
            Explore how leading media outlets worldwide are covering SIG Day (The Global No Enemy Festival) and its transformative impact on global peace.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {coverageTypes.map(type => (
            <button key={type} onClick={() => setSelectedType(type)}
              className={`px-5 py-2 rounded-full font-cta text-sm font-bold transition-all duration-300 ${selectedType === type ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground border border-border hover:border-primary'}`}>
              {type}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredCoverage.map(item => (
            <div key={item.id} className="bg-card rounded-xl overflow-hidden border border-border transition-all duration-300 hover:shadow-xl hover:border-primary group">
              <div className="relative h-48 overflow-hidden bg-muted">
                <AppImage src={item.thumbnail_url} alt={item.alt_text} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 right-4">
                  <span className="text-xs font-cta font-bold text-white bg-primary px-3 py-1 rounded-full">{item.type}</span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="BuildingOfficeIcon" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-cta font-bold text-muted-foreground">{item.outlet}</span>
                </div>
                <h3 className="text-lg font-headline font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-300">{item.title}</h3>
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                  <span className="font-body">{item.date}</span>
                  <span className="font-body">{item.reach}</span>
                </div>
                <a href={item.url !== '#' ? item.url : undefined} target="_blank" rel="noopener noreferrer"
                  className="w-full px-4 py-3 bg-muted text-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-primary hover:text-primary-foreground flex items-center justify-center space-x-2">
                  <span>View Coverage</span>
                  <Icon name="ArrowTopRightOnSquareIcon" size={16} />
                </a>
              </div>
            </div>
          ))}
          {filteredCoverage.length === 0 && (
            <div className="col-span-3 text-center py-16">
              <Icon name="TvIcon" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground font-body">No media coverage found for this type.</p>
            </div>
          )}
        </div>

        <div className="bg-card rounded-xl p-8 lg:p-12 border border-border text-center">
          <Icon name="ChartBarIcon" size={48} className="text-primary mx-auto mb-6" />
          <h3 className="text-2xl lg:text-3xl font-headline font-bold text-foreground mb-4">Media Impact Dashboard</h3>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto font-body">Track real-time media mentions, sentiment analysis, and global reach metrics across all platforms.</p>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {[
              { label: 'Total Mentions', value: '347', icon: 'ChatBubbleLeftRightIcon' },
              { label: 'Global Reach', value: '2.8M+', icon: 'GlobeAltIcon' },
              { label: 'Positive Sentiment', value: '94%', icon: 'HandThumbUpIcon' },
              { label: 'Media Partners', value: '28', icon: 'BuildingOffice2Icon' },
            ].map((stat, index) => (
              <div key={index} className="bg-muted rounded-lg p-6">
                <Icon name={stat.icon as any} size={32} className="text-primary mx-auto mb-3" />
                <div className="text-3xl font-headline font-bold text-foreground mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-body">{stat.label}</div>
              </div>
            ))}
          </div>
          <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg inline-flex items-center space-x-2">
            <Icon name="PresentationChartLineIcon" size={20} />
            <span>View Full Analytics</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default MediaCoverageSection;